// PricingPage.jsx
import React, { useState, useEffect } from "react";
import "./subscriptions.css";
import Navbar from '../../components/Navbar/Navbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { setCookie } from '../../utils/cookies';
import { getShowPlans, getUpgradePlans } from '../../services/subscriptionApis';

const YellowTick = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="yellow-tick-icon">
    <path d="M13.3 4L6 11.3 2.7 8" stroke="#F0B100" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GreenTick = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="green-tick-icon">
    <path d="M13.3 4L6 11.3 2.7 8" stroke="#00C83C" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PricingPage = () => {
  const [billing, setBilling] = useState("yearly");
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation()

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        // const token = localStorage.getItem('accessToken');
        const isUpgradeFlow = location.state?.currentPlan?.is_upgrade ?? false;

        const res = isUpgradeFlow ? await getUpgradePlans() : await getShowPlans();

        setPlans(res.plans);

        const apiCurrentPlan = res.current_plan || location.state?.currentPlan || null;
        const isUpgradeFromState = location.state?.currentPlan?.is_upgrade ?? false;
        setCurrentPlan(apiCurrentPlan
          ? { ...apiCurrentPlan, is_upgrade: isUpgradeFromState }
          : null
        );
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        // If API fails, still use what was passed from profile page
        setCurrentPlan(location.state?.currentPlan || null);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const isExpired = currentPlan?.is_expired ?? false;

  const formatPrice = (plan) => {
    if (plan.is_freeplan) return `${plan.currency}0`;
    const price = billing === "yearly" ? plan.yearly_price : plan.monthly_price;
    return `${plan.currency}${Math.round(price).toLocaleString("en-IN")}`;
  };

  const periodLabel = billing === "yearly" ? "/ Annual plan" : "/ Month";

  const handlePlanSelect = (plan) => {

    if (plan.is_freeplan || (plan.is_current_plan && !isExpired)) return;
    // if (plan.is_freeplan || plan.is_current_plan) return;

    setCookie('selected_plan_id', String(plan.id), 7);
    setCookie('selected_billing_cycle', billing, 7);

    navigate('/plan-configure', {
      state: {
        planId: plan.id,
        billingCycle: billing,
        currentPlan,
        is_upgrade: currentPlan?.is_upgrade ?? false,
        is_renewal: plan.is_current_plan && isExpired,  // ← add this
      }
    });
  };

  const capitalizeFirst = (str) => {
    if (!str) return '';
    str = str.trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <div className="app">
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <p style={{ color: '#475569', fontFamily: 'Poppins, sans-serif' }}>Loading plans…</p>
        </div>
      </div>
    );
  }

  return (
    <div className='app'>
      <Navbar />
      <div className="pricing-page">

        {/* Hero */}
        <section className="pricing-hero">
          <div className="pricing-badge">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" fill="none" className="hero-icon-price">
              <path d="M8.49992 3.54191C8.50076 3.25859 8.44493 2.97798 8.33572 2.71656C8.22652 2.45514 8.06614 2.21819 7.86403 2.01966C7.66191 1.82113 7.42213 1.66502 7.1588 1.56051C6.89547 1.456 6.6139 1.4052 6.33065 1.41111C6.04739 1.41701 5.76818 1.4795 5.50943 1.59489C5.25069 1.71028 5.01762 1.87624 4.82395 2.08303C4.63029 2.28981 4.47992 2.53323 4.38171 2.79897C4.28349 3.06472 4.23941 3.34742 4.25205 3.63045C3.83569 3.7375 3.44915 3.9379 3.12171 4.21646C2.79427 4.49502 2.53451 4.84444 2.3621 5.23826C2.1897 5.63207 2.10917 6.05996 2.12661 6.4895C2.14406 6.91905 2.25902 7.33899 2.4628 7.71753C2.10451 8.00861 1.82276 8.38282 1.64208 8.80761C1.46139 9.2324 1.38723 9.69491 1.42605 10.1549C1.46487 10.6149 1.6155 11.0584 1.86482 11.4469C2.11415 11.8354 2.45462 12.1571 2.85663 12.384C2.80699 12.7681 2.83661 13.1583 2.94367 13.5305C3.05074 13.9027 3.23296 14.249 3.4791 14.548C3.72524 14.847 4.03006 15.0923 4.37474 15.2689C4.71942 15.4455 5.09665 15.5495 5.48311 15.5746C5.86958 15.5997 6.25708 15.5453 6.62169 15.4147C6.98631 15.2842 7.32028 15.0802 7.603 14.8156C7.88572 14.5509 8.11117 14.2311 8.26544 13.8758C8.4197 13.5206 8.49951 13.1375 8.49992 12.7502V3.54191Z" stroke="#615FFF" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.50001 3.54191C8.49917 3.25859 8.555 2.97798 8.66421 2.71656C8.77341 2.45514 8.93379 2.21819 9.1359 2.01966C9.33802 1.82113 9.57779 1.66502 9.84113 1.56051C10.1045 1.456 10.386 1.4052 10.6693 1.41111C10.9525 1.41701 11.2317 1.4795 11.4905 1.59489C11.7492 1.71028 11.9823 1.87624 12.176 2.08303C12.3696 2.28981 12.52 2.53323 12.6182 2.79897C12.7164 3.06472 12.7605 3.34742 12.7479 3.63045C13.1642 3.7375 13.5508 3.9379 13.8782 4.21646C14.2057 4.49502 14.4654 4.84444 14.6378 5.23826C14.8102 5.63207 14.8908 6.05996 14.8733 6.4895C14.8559 6.91905 14.7409 7.33899 14.5371 7.71753C14.8954 8.00861 15.1772 8.38282 15.3579 8.80761C15.5385 9.2324 15.6127 9.69491 15.5739 10.1549C15.5351 10.6149 15.3844 11.0584 15.1351 11.4469C14.8858 11.8354 14.5453 12.1571 14.1433 12.384C14.1929 12.7681 14.1633 13.1583 14.0563 13.5305C13.9492 13.9027 13.767 14.249 13.5208 14.548C13.2747 14.847 12.9699 15.0923 12.6252 15.2689C12.2805 15.4455 11.9033 15.5495 11.5168 15.5746C11.1304 15.5997 10.7428 15.5453 10.3782 15.4147C10.0136 15.2842 9.67965 15.0802 9.39693 14.8156C9.11421 14.5509 8.88876 14.2311 8.73449 13.8758C8.58023 13.5206 8.50042 13.1375 8.50001 12.7502V3.54191Z" stroke="#615FFF" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>POWERED BY SALESROUTE AI</span>
          </div>
          <h1 className="hero-h1">Smarter Sales.</h1>
          <h1 className="hero-h2">Less Effort. Powered by AI.</h1>
          <p className="hero-desc">
            Salesroute uses AI to plan your day, optimize routes, and guide your sales team — automatically.
          </p>



          <div className="toggle-wrap">
            <div className="billing-toggle">
              <div className={`toggle-opt ${billing === "yearly" ? "active" : ""}`} onClick={() => setBilling("yearly")}>
                Yearly <span className="save-label">(Save 30%)</span>
              </div>
              <div className={`toggle-opt ${billing === "monthly" ? "active" : ""}`} onClick={() => setBilling("monthly")}>
                Monthly
              </div>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="pricing-cards">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`pricing-card ${plan.is_advanced ? "card-advanced" : ""} ${plan.is_freeplan ? "card-free-disabled" : ""}`}
            >
              {plan.is_advanced && (
                <div className="popular-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" className="card-popular-icon">
                    <path d="M4.96847 7.75012C4.92383 7.57709 4.83364 7.41918 4.70728 7.29282C4.58092 7.16646 4.42301 7.07626 4.24997 7.03162L1.18247 6.24062C1.13014 6.22577 1.08407 6.19425 1.05128 6.15085C1.01848 6.10744 1.00073 6.05453 1.00073 6.00012C1.00073 5.94572 1.01848 5.89281 1.05128 5.8494C1.08407 5.806 1.13014 5.77448 1.18247 5.75962L4.24997 4.96812C4.42294 4.92353 4.58082 4.83341 4.70717 4.70714C4.83353 4.58088 4.92375 4.42307 4.96847 4.25012L5.75947 1.18262C5.77417 1.13008 5.80566 1.0838 5.84913 1.05082C5.8926 1.01785 5.94566 1 6.00022 1C6.05478 1 6.10784 1.01785 6.15131 1.05082C6.19478 1.0838 6.22627 1.13008 6.24097 1.18262L7.03147 4.25012C7.07611 4.42316 7.1663 4.58107 7.29266 4.70743C7.41902 4.83379 7.57693 4.92399 7.74997 4.96862L10.8175 5.75912C10.8702 5.77367 10.9167 5.80513 10.9499 5.84866C10.983 5.8922 11.001 5.94541 11.001 6.00012C11.001 6.05484 10.983 6.10805 10.9499 6.15159C10.9167 6.19512 10.8702 6.22657 10.8175 6.24112L7.74997 7.03162C7.57693 7.07626 7.41902 7.16646 7.29266 7.29282C7.1663 7.41918 7.07611 7.57709 7.03147 7.75012L6.24047 10.8176C6.22577 10.8702 6.19428 10.9165 6.15081 10.9494C6.10734 10.9824 6.05428 11.0002 5.99972 11.0002C5.94516 11.0002 5.8921 10.9824 5.84863 10.9494C5.80516 10.9165 5.77367 10.8702 5.75897 10.8176L4.96847 7.75012Z" fill="white" fillOpacity="0.2" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 1.5V3.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11 2.5H9" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 8.5V9.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.5 9H1.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Most Popular for Growing Teams</span>
                </div>
              )}

              <h4 className={`card-label ${plan.is_advanced ? "label-advanced" : ""}`}>
                {plan.display_name.toUpperCase()}
              </h4>

              <div className={plan.is_freeplan ? "pricing-card-Text-free" : "pricing-card-Text"}>
                <span>
                  {plan.is_freeplan
                    ? "Everything you need to run your business. Simple, essential, and effective."
                    : plan.is_advanced
                      ? "Supercharge your operations with intelligent optimization. Scaling sales teams that need automation, AI insights, and route optimization"
                      : "Salesroute Core — Essential field sales tools for startups, and teams getting started with field sales"}
                </span>
              </div>

              <div className="price-row">
                <span className="price-value">{formatPrice(plan)}</span>
                <span className="price-period"> {periodLabel}</span>
              </div>

              <p className="badge-text">{capitalizeFirst(plan.badge_text)}</p>

              {plan.is_freeplan && (
                <div className="free-chips">
                  <span className="chip"><GreenTick /> No Credit Card Required</span>
                  <span className="chip"><GreenTick /> Cancel Anytime</span>
                </div>
              )}

              <button
                className={`plan-btn ${
                  plan.is_freeplan || (plan.is_current_plan && !isExpired)
                    ? "btn-blue btn-disabled"
                    : "btn-yellow"
                }`}
                onClick={() =>
                  !(plan.is_freeplan || (plan.is_current_plan && !isExpired)) &&
                  handlePlanSelect(plan)
                }
                disabled={plan.is_freeplan || (plan.is_current_plan && !isExpired)}
              >
                {plan.is_current_plan && !isExpired
                  ? "Current Plan"
                  : plan.is_current_plan && isExpired
                  ? "Renew Plan"          // ← expired current plan becomes clickable
                  : plan.is_freeplan
                  ? "Free Plan"
                  : plan.is_advanced
                  ? "Get Started With AI+"
                  : "Get Started"}
              </button>

              <ul className="feature-list">
                {plan.features.map((f) => (
                  <li key={f}>
                    <YellowTick />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Footer */}
        <footer className="pricing-footer">
          <div className="pricing-footer-inner">
            <div className="footer-item">
              <div className="footer-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className="security-icon">
                  <path d="M16.6666 10.8335C16.6666 15.0002 13.7499 17.0835 10.2833 18.2919C10.1017 18.3534 9.90453 18.3505 9.72492 18.2835C6.24992 17.0835 3.33325 15.0002 3.33325 10.8335V5.00021C3.33325 4.7792 3.42105 4.56724 3.57733 4.41096C3.73361 4.25468 3.94557 4.16688 4.16659 4.16688C5.83325 4.16688 7.91658 3.16688 9.36658 1.90021C9.54313 1.74938 9.76771 1.6665 9.99992 1.6665C10.2321 1.6665 10.4567 1.74938 10.6333 1.90021C12.0916 3.17521 14.1666 4.16688 15.8333 4.16688C16.0543 4.16688 16.2662 4.25468 16.4225 4.41096C16.5788 4.56724 16.6666 4.7792 16.6666 5.00021V10.8335Z" stroke="#0080D7" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.5 10.0002L9.16667 11.6668L12.5 8.3335" stroke="#0080D7" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4 className="footer-title">ENTERPRISE SECURITY</h4>
              <p className="footer-sub">Safe & Private</p>
            </div>
            <div className="footer-item">
              <div className="footer-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className="lightning-icon">
                  <path d="M3.33349 11.6667C3.17579 11.6673 3.02118 11.623 2.88762 11.5392C2.75405 11.4554 2.64702 11.3354 2.57895 11.1931C2.51088 11.0509 2.48457 10.8922 2.50307 10.7356C2.52158 10.579 2.58414 10.4309 2.68349 10.3084L10.9335 1.80839C10.9954 1.73696 11.0797 1.68868 11.1726 1.6715C11.2656 1.65431 11.3616 1.66923 11.4449 1.71381C11.5283 1.75838 11.594 1.82997 11.6313 1.91681C11.6686 2.00365 11.6752 2.10059 11.6502 2.19172L10.0502 7.20839C10.003 7.33466 9.98713 7.47049 10.004 7.60423C10.0208 7.73797 10.0699 7.86562 10.1469 7.97624C10.2239 8.08686 10.3266 8.17715 10.4462 8.23935C10.5658 8.30156 10.6987 8.33383 10.8335 8.33339H16.6668C16.8245 8.33285 16.9791 8.37707 17.1127 8.46091C17.2463 8.54474 17.3533 8.66476 17.4214 8.80701C17.4894 8.94926 17.5157 9.1079 17.4972 9.26451C17.4787 9.42111 17.4162 9.56926 17.3168 9.69172L9.06682 18.1917C9.00494 18.2632 8.92061 18.3114 8.82767 18.3286C8.73474 18.3458 8.63872 18.3309 8.55538 18.2863C8.47204 18.2417 8.40634 18.1701 8.36904 18.0833C8.33175 17.9965 8.32509 17.8995 8.35016 17.8084L9.95016 12.7917C9.99734 12.6655 10.0132 12.5296 9.99633 12.3959C9.97948 12.2621 9.93044 12.1345 9.85341 12.0239C9.77639 11.9132 9.67368 11.823 9.55409 11.7608C9.43451 11.6985 9.30162 11.6663 9.16682 11.6667H3.33349Z" stroke="#34A853" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4 className="footer-title">NO HIDDEN CHARGES</h4>
              <p className="footer-sub">Flat Pricing</p>
            </div>
            <div className="footer-item">
              <div className="footer-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className="spark-icon">
                  <path d="M8.28062 12.9167C8.20622 12.6283 8.0559 12.3651 7.8453 12.1545C7.6347 11.9439 7.37151 11.7936 7.08312 11.7192L1.97062 10.4009C1.8834 10.3761 1.80663 10.3236 1.75196 10.2512C1.6973 10.1789 1.66772 10.0907 1.66772 10C1.66772 9.90937 1.6973 9.82118 1.75196 9.74884C1.80663 9.6765 1.8834 9.62397 1.97062 9.59921L7.08312 8.28004C7.37141 8.20572 7.63453 8.05552 7.84512 7.84508C8.05571 7.63463 8.20609 7.37162 8.28062 7.08338L9.59895 1.97088C9.62346 1.88331 9.67594 1.80616 9.74839 1.75121C9.82084 1.69625 9.90927 1.6665 10.0002 1.6665C10.0911 1.6665 10.1796 1.69625 10.252 1.75121C10.3245 1.80616 10.3769 1.88331 10.4015 1.97088L11.719 7.08338C11.7934 7.37177 11.9437 7.63496 12.1543 7.84556C12.3649 8.05616 12.6281 8.20648 12.9165 8.28088L18.029 9.59838C18.1169 9.62263 18.1944 9.67505 18.2497 9.74761C18.3049 9.82016 18.3348 9.90884 18.3348 10C18.3348 10.0912 18.3049 10.1799 18.2497 10.2525C18.1944 10.325 18.1169 10.3775 18.029 10.4017L12.9165 11.7192C12.6281 11.7936 12.3649 11.9439 12.1543 12.1545C11.9437 12.3651 11.7934 12.6283 11.719 12.9167L10.4006 18.0292C10.3761 18.1168 10.3236 18.1939 10.2512 18.2489C10.1787 18.3038 10.0903 18.3336 9.99937 18.3336C9.90844 18.3336 9.82 18.3038 9.74756 18.2489C9.67511 18.1939 9.62263 18.1168 9.59812 18.0292L8.28062 12.9167Z" stroke="#F0B100" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4 className="footer-title">CANCEL ANYTIME</h4>
              <p className="footer-sub">Flexible Terms</p>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default PricingPage;